import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Globe, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function GlobalStandards() {
  const { loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  useEffect(() => {
    if (authLoading) return;
    api.get("/devices").then(({ data }) => {
      setProjects(data); if (data[0]) setPid(data[0].project_id);
    }).catch(() => {});
  }, [authLoading]);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6">
          <div className="vq-overline mb-2 flex items-center gap-2"><Globe className="w-3 h-3" /> Global standards</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
            QMSR · IVDR · ISO/IEC 42001 · EU AI Act FRIA
          </h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            Reference catalogs and per-project checklists for the four largest regulatory shifts of 2026.
            Draft scaffolds only — qualified reviewer sign-off required.
          </p>
        </div>

        <div className="mb-4 max-w-md">
          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Device project</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="gs-project-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.device_name || p.project_id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="qmsr">
          <TabsList data-testid="gs-tabs">
            <TabsTrigger value="qmsr" data-testid="tab-qmsr">QMSR (US)</TabsTrigger>
            <TabsTrigger value="ivdr" data-testid="tab-ivdr">IVDR (EU)</TabsTrigger>
            <TabsTrigger value="iso42001" data-testid="tab-iso42001">ISO/IEC 42001</TabsTrigger>
            <TabsTrigger value="fria" data-testid="tab-fria">EU AI Act FRIA</TabsTrigger>
          </TabsList>
          <TabsContent value="qmsr"><QMSRTab /></TabsContent>
          <TabsContent value="ivdr"><IVDRTab pid={pid} /></TabsContent>
          <TabsContent value="iso42001"><ISO42001Tab /></TabsContent>
          <TabsContent value="fria"><FRIATab pid={pid} /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function QMSRTab() {
  const [d, setD] = useState({ mappings: [] });
  useEffect(() => { api.get("/qmsr/mapping").then(({ data }) => setD(data)); }, []);
  return (
    <div className="mt-6 space-y-3">
      <div className="vq-card p-3 border-l-4 border-l-blue-500 bg-blue-50 text-xs text-blue-900">{d.note}</div>
      <div className="vq-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 w-24">21 CFR 820</th>
              <th className="text-left px-3 py-2">Legacy term</th>
              <th className="text-left px-3 py-2">QMSR term (ISO 13485)</th>
              <th className="text-left px-3 py-2">Impact</th>
            </tr>
          </thead>
          <tbody data-testid="qmsr-tbody">
            {d.mappings.map((m) => (
              <tr key={m.clause_820} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{m.clause_820}</td>
                <td className="px-3 py-2 text-slate-700">{m.old_term}</td>
                <td className="px-3 py-2 font-medium">{m.qmsr_term}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{m.impact}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const STATUSES = ["not_started", "in_progress", "complete", "not_applicable"];

function IVDRTab({ pid }) {
  const [data, setData] = useState({ items: [], counts: {}, readiness_pct: 0 });
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ status: "not_started", evidence_link: "", notes: "" });

  const load = useCallback(() => {
    if (!pid) return;
    api.get(`/ivdr/checklist/${pid}`).then(({ data }) => setData(data)).catch(() => {});
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const openEditor = (i) => {
    setEdit(i);
    setForm({ status: i.status, evidence_link: i.evidence_link || "", notes: i.notes || "" });
  };
  const save = async () => {
    try {
      await api.post("/ivdr/evidence", { project_id: pid, clause: edit.clause, ...form });
      toast.success("Saved."); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  return (
    <div className="mt-6 space-y-3">
      <div className="vq-card p-3 flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-700"
             data-testid="ivdr-readiness-pct">{data.readiness_pct}%</div>
        <div className="text-xs text-slate-500">EU IVDR 2017/746 evidence checklist</div>
      </div>
      <div className="vq-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 w-32">Clause</th>
              <th className="text-left px-3 py-2 w-32">Topic</th>
              <th className="text-left px-3 py-2">Requirement</th>
              <th className="text-left px-3 py-2 w-40">Status</th>
              <th className="text-right px-3 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody data-testid="ivdr-tbody">
            {data.items.map((i) => (
              <tr key={i.clause} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{i.clause}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{i.topic}</td>
                <td className="px-3 py-2">{i.title}</td>
                <td className="px-3 py-2 text-xs">{i.status.replace("_", " ")}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" data-testid={`ivdr-edit-${i.clause}`} onClick={() => openEditor(i)}>Edit</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{edit?.clause} — {edit?.title}</DialogTitle>
            <DialogDescription>Update IVDR evidence and status.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="ivdr-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Evidence link</Label><Input data-testid="ivdr-evidence" value={form.evidence_link} onChange={(e) => setForm({ ...form, evidence_link: e.target.value })} /></div>
            <div><Label>Notes</Label><Textarea data-testid="ivdr-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
            <Button data-testid="ivdr-save" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ISO42001Tab() {
  const [d, setD] = useState({ categories: [] });
  useEffect(() => { api.get("/iso42001/catalog").then(({ data }) => setD(data)); }, []);
  return (
    <div className="mt-6">
      <div className="vq-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 w-24">Clause</th>
              <th className="text-left px-3 py-2 w-40">Topic</th>
              <th className="text-left px-3 py-2">Requirement</th>
            </tr>
          </thead>
          <tbody data-testid="iso42001-tbody">
            {d.categories.map((c) => (
              <tr key={c.clause} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{c.clause}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{c.topic}</td>
                <td className="px-3 py-2">{c.title}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const RISK_LEVELS = ["low", "moderate", "high", "unacceptable"];

function FRIATab({ pid }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [level, setLevel] = useState("low");
  const [records, setRecords] = useState([]);

  useEffect(() => {
    api.get("/eu-ai-act/fria/questions").then(({ data }) => setQuestions(data.questions));
  }, []);
  useEffect(() => {
    if (!pid) return;
    api.get(`/eu-ai-act/fria?project_id=${pid}`).then(({ data }) => setRecords(data)).catch(() => {});
  }, [pid]);

  const submit = async () => {
    try {
      const { data } = await api.post("/eu-ai-act/fria", {
        project_id: pid, answers, residual_risk_level: level,
      });
      toast.success(`FRIA saved: ${data.fria_id}`);
      setAnswers({});
      setRecords((prev) => [data, ...prev]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="vq-card p-3 border-l-4 border-l-blue-500 bg-blue-50 text-xs text-blue-900">
        EU AI Act Article 27 — Fundamental Rights Impact Assessment. Required for high-risk AI system deployers
        prior to first deployment.
      </div>
      <div className="vq-card p-4 space-y-3">
        {questions.map((q) => (
          <div key={q.id}>
            <Label className="text-xs">{q.text}</Label>
            <Textarea data-testid={`fria-${q.id}`} rows={2}
                      value={answers[q.id] || ""}
                      onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} />
          </div>
        ))}
        <div>
          <Label>Residual risk level</Label>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger data-testid="fria-level"><SelectValue /></SelectTrigger>
            <SelectContent>{RISK_LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="fria-save" onClick={submit} disabled={!pid || Object.keys(answers).length < 3}>Save FRIA</Button>
      </div>
      <div className="vq-card overflow-hidden">
        <div className="p-3 border-b bg-slate-50 font-display font-bold text-sm">Saved FRIAs</div>
        <table className="w-full text-sm">
          <tbody data-testid="fria-tbody">
            {records.length === 0 && <tr><td className="text-center px-3 py-6 text-slate-400">No FRIAs yet.</td></tr>}
            {records.map((r) => (
              <tr key={r.fria_id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{r.fria_id}</td>
                <td className="px-3 py-2 text-xs">{r.residual_risk_level}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
